import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface ForecastRow {
  readonly forecastId: string;
  readonly forecastCode: string;
  readonly forecastName: string;
  readonly forecastType: string;
  readonly method: string;
  readonly horizon: number;
  readonly horizonUnit: string;
  readonly runStatus: string;
  readonly confidence: string | null;
  readonly accuracy: string | null;
  readonly isAdvisory: boolean;
  readonly status: string;
}

const FORECAST_TYPES = ['CAMPAIGN', 'BUDGET', 'VENDOR_RISK', 'APPROVAL_DELAY', 'ASSET_UTILIZATION', 'RESOURCE', 'DEMAND'];
const METHODS = ['LINEAR_REGRESSION', 'MOVING_AVERAGE', 'EXPONENTIAL_SMOOTHING', 'SEASONAL', 'RATE_OF_CHANGE', 'WEIGHTED_AVERAGE'];
const HORIZON_UNITS = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'];
const PAGE = 25;

/**
 * M41 — Forecasting Dashboard (§26 / §11). The predictive-analytics surface over
 * the statistical forecast models (T539). Define a forecast (type + one of the
 * six statistical methods — no ML, §31), then run it against a historical series
 * to produce an advisory projection (BR-03 — forecasts are advisory only, never
 * auto-actioned). Viewing needs FORECAST_VIEW, defining needs FORECAST_MANAGE,
 * running needs FORECAST_RUN.
 */
export function ForecastingDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ForecastRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [forecastType, setForecastType] = useState('CAMPAIGN');
  const [method, setMethod] = useState('LINEAR_REGRESSION');
  const [horizon, setHorizon] = useState('3');
  const [horizonUnit, setHorizonUnit] = useState('MONTH');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [series, setSeries] = useState<Record<string, string>>({});

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('forecastType', typeFilter);
    if (methodFilter) params.set('method', methodFilter);
    if (search) params.set('search', search);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: ForecastRow[]; total: number }>(`/v1/data-lake/forecasts?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, methodFilter, search, page, reload]);

  const define = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m41.forecasting.nameRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/forecasts', {
        method: 'POST',
        body: { forecastName: name.trim(), forecastType, method, horizon: Number(horizon), horizonUnit },
        token,
      });
      setNotice(t('m41.forecasting.defined'));
      setName('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.forecasting.notAuthDefine'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const run = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const parsed = (series[id] ?? '')
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '')
      .map((v) => Number(v));
    if (parsed.length === 0 || parsed.some((n) => !Number.isFinite(n))) {
      setActionError(t('m41.forecasting.seriesRequired'));
      return;
    }
    try {
      await apiRequest(`/v1/data-lake/forecasts/${id}/run`, { method: 'POST', body: { series: parsed }, token });
      setNotice(t('m41.forecasting.generated'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.forecasting.notAuthRun'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const startEdit = (f: ForecastRow): void => {
    setEditingId(f.forecastId);
    setEditName(f.forecastName);
    setActionError(null);
    setNotice(null);
  };

  const saveEdit = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    if (!editName.trim()) { setActionError(t('m41.forecasting.nameRequired')); return; }
    try {
      await apiRequest(`/v1/data-lake/forecasts/${id}`, { method: 'PUT', body: { forecastName: editName.trim() }, token });
      setNotice(t('m41.forecasting.updated'));
      setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.forecasting.notAuthUpdate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/forecasts/${id}`, { method: 'DELETE', token });
      setNotice(t('m41.forecasting.deleted'));
      if (editingId === id) setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.forecasting.notAuthDelete'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="forecasting-dashboard">
      <h1>{t('m41.forecasting.title')}</h1>
      <p aria-label="advisory-note">{t('m41.forecasting.advisory')}</p>

      <section aria-label="forecast-define">
        <h2>{t('m41.forecasting.define')}</h2>
        <form aria-label="define-form" onSubmit={(e) => void define(e)}>
          <label>{t('m41.common.name')}<input aria-label="field-forecast-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m41.common.type')}
            <select aria-label="field-forecast-type" value={forecastType} onChange={(e) => setForecastType(e.target.value)}>
              {FORECAST_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.forecasting.method')}
            <select aria-label="field-method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>{t('m41.forecasting.horizon')}<input aria-label="field-horizon" type="number" value={horizon} onChange={(e) => setHorizon(e.target.value)} /></label>
          <label>{t('m41.forecasting.horizonUnit')}
            <select aria-label="field-horizon-unit" value={horizonUnit} onChange={(e) => setHorizonUnit(e.target.value)}>
              {HORIZON_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-define">{t('m41.common.define')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="forecast-action-error">{actionError}</p>}
        {notice && <p aria-label="forecast-notice">{notice}</p>}
      </section>

      <section aria-label="forecast-filters">
        <label>{t('m41.common.type')}
          <select aria-label="filter-forecast-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {FORECAST_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m41.forecasting.method')}
          <select aria-label="filter-method" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>{t('common.search')}<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="forecast-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="forecast-total">{t('m41.forecasting.total', { count: total })}</p>
          <DataTable
            ariaLabel="forecast-table"
            rows={rows}
            rowKey={(f) => f.forecastId}
            emptyMessage={t('m41.forecasting.empty')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (f) => `forecast-code-${f.forecastId}`, render: (f) => f.forecastCode },
              { key: 'name', header: t('m41.common.name'), render: (f) => f.forecastName },
              { key: 'type', header: t('m41.common.type'), render: (f) => f.forecastType },
              { key: 'method', header: t('m41.forecasting.method'), render: (f) => f.method },
              { key: 'horizon', header: t('m41.forecasting.horizon'), render: (f) => <>{f.horizon} {f.horizonUnit}</> },
              { key: 'run', header: t('m41.forecasting.runCol'), cellAriaLabel: (f) => `run-status-${f.forecastId}`, render: (f) => f.runStatus },
              { key: 'confidence', header: t('m41.common.confidence'), render: (f) => decimal(f.confidence) },
              { key: 'accuracy', header: t('m41.forecasting.accuracy'), cellAriaLabel: (f) => `accuracy-${f.forecastId}`, render: (f) => f.accuracy ?? '—' },
              { key: 'generate', header: t('m41.common.generate'), render: (f) => (
                <>
                  <input aria-label={`series-${f.forecastId}`} placeholder="10, 12, 15"
                    value={series[f.forecastId] ?? ''} onChange={(e) => setSeries((m) => ({ ...m, [f.forecastId]: e.target.value }))} />
                  <Button variant="secondary" type="button" aria-label={`run-${f.forecastId}`} onClick={() => void run(f.forecastId)}>{t('m41.common.run')}</Button>
                </>
              ) },
              { key: 'actions', header: t('m41.common.actions'), render: (f) => (
                editingId === f.forecastId ? (
                  <>
                    <input aria-label={`edit-name-${f.forecastId}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    <Button variant="primary" type="button" aria-label={`save-${f.forecastId}`} onClick={() => void saveEdit(f.forecastId)}>{t('common.save')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-${f.forecastId}`} onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" type="button" aria-label={`edit-${f.forecastId}`} onClick={() => startEdit(f)}>{t('m41.common.edit')}</Button>
                    <Button variant="danger" type="button" aria-label={`delete-${f.forecastId}`} onClick={() => void remove(f.forecastId)}>{t('common.delete')}</Button>
                  </>
                )
              ) },
            ]}
          />

          <nav aria-label="forecast-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
